import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import BodyMap, { AREA_LABELS } from "@/components/BodyMap";
import InvitationInbox from "@/components/InvitationInbox";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Activity, Clock, Users, Flame, Heart, LineChart as LineIcon, Building2,
  CalendarDays, ChevronLeft, ChevronRight, Coffee, HandMetal,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const EXAM_TYPES = ["Abdominal", "OB/GYN", "Vascular", "Cardiac", "MSK", "Breast", "Thyroid", "Pediatric"];

function formatDate(d) { return d.toISOString().slice(0, 10); }
function humanDate(d) {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export default function SonographerDashboard() {
  const { user } = useAuth();
  const [entryDate, setEntryDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [hours, setHours] = useState(8);          // default 8 per product spec
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [patients, setPatients] = useState(12);
  const [exams, setExams] = useState([]);
  const [areaScores, setAreaScores] = useState({});
  const [notes, setNotes] = useState("");
  const [summary, setSummary] = useState(null);
  const [wellness, setWellness] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, w, c] = await Promise.all([
      api.get("/pain-entries/summary"),
      api.get("/wellness/me"),
      api.get("/clinics/mine").catch(() => ({ data: null })),
    ]);
    setSummary(s.data); setWellness(w.data); setClinic(c.data);
  };
  useEffect(() => { load(); }, []);

  // Effective time = hours - breaks/60, clamped to [0, hours]
  const effectiveHours = useMemo(() => {
    const h = Number(hours) || 0;
    const b = Number(breakMinutes) || 0;
    return Math.max(0, +(h - b / 60).toFixed(2));
  }, [hours, breakMinutes]);

  const toggleExam = (t) => setExams(exams.includes(t) ? exams.filter((x) => x !== t) : [...exams, t]);
  const updateAreaScore = (area, score) => setAreaScores({ ...areaScores, [area]: score });
  const handleAreas = (next) => {
    setSelectedAreas(next);
    const ns = { ...areaScores };
    next.forEach((a) => { if (ns[a] == null) ns[a] = 3; });
    Object.keys(ns).forEach((k) => { if (!next.includes(k)) delete ns[k]; });
    setAreaScores(ns);
  };
  const clearArea = (area) => {
    setSelectedAreas((s) => s.filter((a) => a !== area));
    setAreaScores((s) => { const c = { ...s }; delete c[area]; return c; });
  };

  const submit = async () => {
    setErr(""); setMsg(""); setBusy(true);
    try {
      const locations = selectedAreas.map((a) => ({ area: a, score: areaScores[a] ?? 3 }));
      await api.post("/pain-entries", {
        locations,
        hours_scanned: effectiveHours,
        patients_scanned: Number(patients),
        exam_types: exams, notes,
        entry_date: formatDate(entryDate),
      });
      const today = formatDate(new Date()) === formatDate(entryDate);
      setMsg(today ? "Entry saved. Keep it up!" : `Entry saved for ${humanDate(entryDate)}.`);
      setSelectedAreas([]); setAreaScores({}); setExams([]); setNotes("");
      setHours(8); setBreakMinutes(0);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const shiftDay = (delta) => {
    const d = new Date(entryDate);
    d.setDate(d.getDate() + delta);
    if (d > new Date()) return;
    setEntryDate(d);
  };
  const isToday = formatDate(entryDate) === formatDate(new Date());

  return (
    <div>
      <InvitationInbox onChange={load} />
      <div className="mb-6">
        <span className="pill">Sonographer</span>
        <h1 className="mt-2 text-3xl font-bold">Hi {user?.name}, ready for today&apos;s check-in?</h1>
      </div>

      {/* TOP STRIP — hours + breaks + patients + date all at the top of the screen */}
      <div className="card-elev mb-6" data-testid="worklog-strip">
        <div className="grid md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Hours worked
            </label>
            <input
              type="number" step="0.5" min="0" max="24" value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 text-lg font-semibold focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="input-hours"
            />
            <div className="text-[11px] text-zinc-400 mt-1">Default 8 h — override anytime.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1">
              <Coffee className="w-3.5 h-3.5" /> Break minutes
            </label>
            <input
              type="number" min="0" max="480" step="5" value={breakMinutes}
              onChange={(e) => setBreakMinutes(e.target.value)}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 text-lg font-semibold focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="input-breaks"
              placeholder="0"
            />
            <div className="text-[11px] text-zinc-400 mt-1">Subtracted from hours worked.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Patients scanned
            </label>
            <input
              type="number" min="0" value={patients}
              onChange={(e) => setPatients(e.target.value)}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 text-lg font-semibold focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="input-patients"
            />
          </div>
          <div
            className="h-full rounded-md p-3 text-center"
            style={{ background: "var(--gold-light)" }}
            data-testid="effective-time-card"
          >
            <div className="text-[11px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--gold-hover)" }}>Effective scan time</div>
            <div className="mt-1 text-3xl font-black" style={{ color: "var(--gold-hover)" }} data-testid="effective-time-value">
              {effectiveHours} h
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">= {hours || 0} h − {breakMinutes || 0} min break</div>
          </div>
        </div>

        {/* Date row */}
        <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
              <CalendarDays className="w-4 h-4" style={{ color: "var(--gold-hover)" }} />
            </span>
            <div className="text-sm">
              <div className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-500">Logging for</div>
              <div className="font-bold" data-testid="entry-date-label">
                {isToday ? "Today" : humanDate(entryDate)}
                {!isToday && <span className="text-zinc-400 font-medium ml-2 text-xs">(backfill)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shiftDay(-1)} className="w-10 h-10 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center" data-testid="entry-date-prev" aria-label="Previous day">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button className="btn-outline h-10 px-4 text-sm" data-testid="entry-date-picker">
                  <CalendarDays className="w-4 h-4 mr-2" />Pick a date
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="end">
                <Calendar
                  mode="single"
                  selected={entryDate}
                  onSelect={(d) => { if (d) { setEntryDate(d); setCalendarOpen(false); } }}
                  disabled={(d) => d > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <button onClick={() => shiftDay(1)} disabled={isToday} className="w-10 h-10 rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center" data-testid="entry-date-next" aria-label="Next day">
              <ChevronRight className="w-4 h-4" />
            </button>
            {!isToday && (
              <button onClick={() => setEntryDate(new Date())} className="btn-ghost text-sm" data-testid="entry-date-today">Today</button>
            )}
          </div>
        </div>
      </div>

      {/* Clinic membership card — small, at top */}
      <div className="card-elev mb-6 flex flex-wrap items-center gap-4 justify-between" data-testid="clinic-membership">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
            <Building2 className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          </span>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Clinic membership</div>
            <div className="font-bold">{clinic?.name || "Independent (no clinic)"}</div>
          </div>
        </div>
        <div className="flex gap-8 text-sm">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Status</div>
            <div className="mt-0.5 font-semibold capitalize" data-testid="membership-status">{clinic?.membership_status || (clinic ? "active" : "not linked")}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Joined</div>
            <div className="mt-0.5 font-semibold" data-testid="membership-joined">{(clinic?.joined_at || user?.created_at || "").slice(0, 10) || "—"}</div>
          </div>
        </div>
      </div>

      {/* BODY MAP — the main focus. Large, centered. Pain input reveals per-region on click. */}
      <div className="grid lg:grid-cols-5 gap-6" data-testid="pain-tracker-area">
        <div className="lg:col-span-3">
          <BodyMap selected={selectedAreas} onChange={handleAreas} large />
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="card-elev" data-testid="pain-input-panel">
            <div className="flex items-center gap-2">
              <HandMetal className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
              <h3 className="font-bold text-lg">Pain intensity</h3>
            </div>
            {selectedAreas.length === 0 ? (
              <div
                className="mt-4 text-sm text-zinc-500 italic border border-dashed border-zinc-200 rounded-lg px-4 py-6 text-center"
                data-testid="no-pain-hint"
              >
                Tap a region on the body map to score it 0–10.<br />
                Nothing hurting today? Just hit save to log a pain-free day.
              </div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="pain-input-list">
                {selectedAreas.map((a) => (
                  <div key={a} className="rounded-lg border border-zinc-200 px-3 py-2" data-testid={`area-score-${a}`}>
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>{AREA_LABELS[a] || a.replace("_", " ")}</span>
                      <button onClick={() => clearArea(a)} className="text-xs text-zinc-400 hover:text-red-600" data-testid={`area-clear-${a}`}>remove</button>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="range" min="0" max="10"
                        value={areaScores[a] ?? 3}
                        onChange={(e) => updateAreaScore(a, Number(e.target.value))}
                        className="flex-1 accent-[#d4af37]"
                        data-testid={`area-slider-${a}`}
                      />
                      <span className="w-8 text-right text-lg font-bold" style={{ color: "var(--gold-hover)" }}>
                        {areaScores[a] ?? 3}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-elev space-y-4" data-testid="context-panel">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-500 mb-2">Exam types performed</div>
              <div className="flex flex-wrap gap-2">
                {EXAM_TYPES.map((t) => (
                  <button
                    type="button" key={t} onClick={() => toggleExam(t)}
                    data-testid={`exam-${t.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${exams.includes(t) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-700"}`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-500">Notes (optional)</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 w-full rounded-md border border-zinc-200 p-3 text-sm" data-testid="input-notes" />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="entry-error">{err}</div>}
            {msg && <div className="text-sm text-green-700" data-testid="entry-success">{msg}</div>}
            <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="submit-entry">
              {busy ? "Saving\u2026" : "Save today\u2019s entry"}
            </button>
          </div>
        </div>
      </div>

      {/* Wellness + KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="kpi-row">
        <div className="card-elev relative overflow-hidden" data-testid="kpi-wellness">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
            <Heart className="w-4 h-4" style={{ color: "var(--gold)" }} /> Wellness Score
          </div>
          <div className="mt-3 text-4xl font-black" style={{ color: "var(--gold-hover)" }}>{wellness?.score ?? 0}</div>
          <div className="text-xs text-zinc-500 mt-1">out of 100</div>
        </div>
        <div className="card-elev" data-testid="kpi-streak">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
            <Flame className="w-4 h-4" style={{ color: "var(--gold)" }} /> Streak
          </div>
          <div className="mt-3 text-3xl font-extrabold">{wellness?.streak_days ?? 0}<span className="text-base text-zinc-400 font-medium ml-1">days</span></div>
        </div>
        <div className="card-elev" data-testid="kpi-entries">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
            <Activity className="w-4 h-4" style={{ color: "var(--gold)" }} /> Entries (30d)
          </div>
          <div className="mt-3 text-3xl font-extrabold">{wellness?.entries_30d ?? 0}</div>
        </div>
        <div className="card-elev" data-testid="kpi-avg-pain">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
            <LineIcon className="w-4 h-4" style={{ color: "var(--gold)" }} /> Avg pain (30d)
          </div>
          <div className="mt-3 text-3xl font-extrabold">{wellness?.avg_pain_30d ?? 0}</div>
        </div>
      </div>

      {/* Mini trend */}
      <div className="card-elev mt-6">
        <h3 className="font-bold mb-4">Recent pain trend</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={summary?.trend || []}>
            <CartesianGrid stroke="#f4f4f5" />
            <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
            <YAxis domain={[0, 10]} stroke="#71717a" fontSize={11} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
            <Line type="monotone" dataKey="score" stroke="#d4af37" strokeWidth={3} dot={{ r: 3, fill: "#d4af37" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
